/* ----------------------------------------------------------------------------
 * Content-engine data source (AI Studio Blog/Articles library).
 *
 * READ-ONLY surface over the module-neutral content engine's HTTP routes
 * (/v1/content/drafts). These endpoints are workspace-scoped SERVER-SIDE — the
 * caller never supplies tenancy — and read ONLY bc_content_drafts. They are
 * entirely separate from the social "Recent content" list
 * (sources-social.ts / bc_social_content_items): blog and social records are
 * never merged here.
 *
 * A saved blog draft VANISHED from the UI after its generation card closed
 * because nothing re-read bc_content_drafts. `listBlogDrafts()` restores
 * discoverability; `getBlogDraft()` reopens one into the existing read-only
 * long-form view (BlogDraftView).
 * -------------------------------------------------------------------------- */
import { api } from '@/lib/api';
import type { BlogDraft, BlogDraftEvidence } from '@/data/copilot';
import { pickBlogEdits, type BlogEditableFields } from '@/data/blog-edit-model';

/** The API projection of a bc_content_drafts row (ContentDraft::apiFromRow).
 *  Deliberately carries NO connector column — none exist on the table. */
interface ContentDraftDto {
  id: number;
  content_type: string;
  source_module: string;
  status: string;
  title: string;
  slug: string;
  meta_title: string;
  meta_description: string;
  body_md: string;
  body_html: string;
  cta: string;
  outline?: unknown;
  seo_evidence?: unknown;
  generated_at?: string;
}

/** A lightweight list-card projection for the Blog/Articles library rail. */
export interface BlogDraftSummary {
  id: number;
  title: string;
  slug: string;
  status: string;
  contentType: string;
  generatedAt: string;
}

function toSummary(d: ContentDraftDto): BlogDraftSummary {
  return {
    id: Number(d.id) || 0,
    title: typeof d.title === 'string' ? d.title : '',
    slug: typeof d.slug === 'string' ? d.slug : '',
    status: typeof d.status === 'string' ? d.status : '',
    contentType: typeof d.content_type === 'string' ? d.content_type : '',
    generatedAt: typeof d.generated_at === 'string' ? d.generated_at : '',
  };
}

/** Normalise the API seo_evidence blob into the FE evidence shape. Permissive:
 *  keep only rows that carry a summary; unknown fields are dropped. */
function toEvidence(raw: unknown): BlogDraftEvidence[] {
  if (!Array.isArray(raw)) return [];
  const out: BlogDraftEvidence[] = [];
  for (const r of raw) {
    if (r && typeof r === 'object') {
      const e = r as Record<string, unknown>;
      const summary = typeof e.summary === 'string' ? e.summary : '';
      if (summary.trim() === '') continue;
      out.push({
        summary,
        source_label: typeof e.source_label === 'string' ? e.source_label : 'Evidence',
        url: typeof e.url === 'string' ? e.url : undefined,
      });
    }
  }
  return out;
}

/** Map an API draft into the BlogDraft the read-only long-form view consumes.
 *  workspace_id / ai_provenance are not exposed on this read surface (tenancy is
 *  server-enforced; provenance is masked), so they are filled with inert
 *  defaults the view never renders. */
function toBlogDraft(d: ContentDraftDto): BlogDraft {
  return {
    id: Number(d.id) || 0,
    workspace_id: 0,
    content_type: 'blog_article',
    title: typeof d.title === 'string' ? d.title : '',
    slug: typeof d.slug === 'string' ? d.slug : '',
    meta_title: typeof d.meta_title === 'string' ? d.meta_title : '',
    meta_description: typeof d.meta_description === 'string' ? d.meta_description : '',
    body_md: typeof d.body_md === 'string' ? d.body_md : '',
    body_html: typeof d.body_html === 'string' ? d.body_html : '',
    cta: typeof d.cta === 'string' ? d.cta : '',
    seo_evidence: toEvidence(d.seo_evidence),
    ai_provenance: { model: '', prompt_version: '' },
    status: 'ready',
  };
}

/** Which domain scope the blog/article library is being viewed under.
 *  DOMAIN ISOLATION (P0 Content Studio): bc_content_drafts carries a populated
 *  project_id, so the library MUST be scoped to the SELECTED domain — otherwise
 *  viewing one domain leaks another domain's drafts. */
export interface BlogDraftScope {
  /** The active "Viewing" project_id, or null when no domain is selected. */
  projectId?: number | null;
  /** Show ONLY drafts with no domain provenance (project_id IS NULL): the
   *  explicit "Across workspace / Unassigned" view. Never mixed with a domain. */
  unassigned?: boolean;
}

/**
 * List saved blog/article drafts for the SELECTED domain, newest first. Reads
 * bc_content_drafts ONLY (never bc_social_content_items). Ready drafts first so
 * the reopenable ones surface at the top; failed/generating rows still listed
 * (with their honest status) but sorted after.
 *
 * DOMAIN SCOPING (server-enforced): the active project_id is threaded as
 * ?project_id=N so the API returns ONLY that domain's drafts (a foreign id is
 * rejected 422 server-side — never a fall-back). `unassigned` sends
 * ?project_id=unassigned for the Across-workspace/Unassigned view. When neither
 * is set (no domain selected) the request carries no project_id and the server
 * fails closed to an EMPTY list — a domain-less call never returns another
 * domain's drafts.
 */
export async function listBlogDrafts(scope: BlogDraftScope = {}): Promise<BlogDraftSummary[]> {
  const query: Record<string, string | number> = { content_type: 'blog_article' };
  if (scope.unassigned) {
    query.project_id = 'unassigned';
  } else if (scope.projectId != null) {
    query.project_id = scope.projectId;
  }
  const res = await api.get<{ ok?: boolean; drafts?: ContentDraftDto[] }>('/content/drafts', query);
  const rows = Array.isArray(res.drafts) ? res.drafts.map(toSummary) : [];
  return rows.sort((a, b) => {
    const ar = a.status === 'ready' ? 0 : 1;
    const br = b.status === 'ready' ? 0 : 1;
    return ar - br; // stable within group (server already returns newest-first)
  });
}

/**
 * Fetch ONE saved blog draft in full (body + SEO + CTA + evidence) for reopen.
 * Workspace-scoped server-side; a foreign-tenant id can never resolve.
 */
export async function getBlogDraft(id: number): Promise<BlogDraft> {
  const res = await api.get<{ ok?: boolean; draft?: ContentDraftDto }>(`/content/drafts/${id}`);
  if (!res.draft) {
    throw new Error('draft_not_found');
  }
  return toBlogDraft(res.draft);
}

/**
 * SOFT delete (reversible) a saved blog/article draft. Workspace-scoped +
 * CSRF-protected SERVER-SIDE: api.del sends the bc_csrf double-submit header on
 * this DELETE, and the caller never supplies tenancy. The server flips the row
 * to status='deleted' (the row is RETAINED and restorable) and excludes it from
 * listBlogDrafts() -- it is a soft delete, not a hard removal. Idempotent: a
 * retry of the same delete resolves as a success no-op. A foreign/absent id
 * resolves as not_found and can never delete another tenant's draft. Reads/writes
 * bc_content_drafts ONLY -- never bc_social_content_items.
 */
export async function deleteBlogDraft(id: number): Promise<void> {
  await api.del<{ ok?: boolean; id?: number; deleted?: boolean }>(`/content/drafts/${id}`);
}

/**
 * Human FINAL edit of a READY blog draft (owner directive item 2). PATCHes ONLY
 * the four human-authored fields (title / slug / meta_title / body_md); the
 * payload is whitelisted client-side (pickBlogEdits) AND again server-side, so no
 * other field (focus_keyphrase, featured image, SEO target, links, meta
 * description, provenance, status) can be touched. Workspace-scoped + CSRF-
 * protected server-side (api.patch sends the bc_csrf double-submit).
 *
 * The server regenerates body_html from the edited markdown and RE-RUNS the same
 * ready/critical-SEO/Yoast validator: a broken edit is REJECTED with HTTP 422
 * (ApiError, code 'validation_failed') and nothing is persisted — the draft keeps
 * its prior valid 'ready' content, so an edit can never bypass a control. On
 * success the SAVED draft is returned (the exact values that will be pushed to
 * WordPress). Reads/writes bc_content_drafts ONLY.
 */
export async function updateBlogDraft(id: number, fields: BlogEditableFields): Promise<BlogDraft> {
  const payload = pickBlogEdits(fields as Record<string, unknown>);
  const res = await api.patch<{ ok?: boolean; draft?: ContentDraftDto }>(`/content/drafts/${id}`, payload);
  if (!res.draft) {
    throw new Error('draft_update_failed');
  }
  return toBlogDraft(res.draft);
}
